import { useCallback, useEffect, useState } from 'react';

/**
 * Progressive web app plumbing: service worker registration, the "new version" signal and the
 * install prompt. The worker itself (public/sw.js) caches no user data; see its header.
 */

// ---- Install prompt ---------------------------------------------------------------------

/** Chrome/Edge/Android event; not in lib.dom. */
export type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let appInstalled = false;
const installListeners = new Set<() => void>();
const emitInstall = () => {
  for (const listener of installListeners) listener();
};

/** Call once at startup: the event often fires before the Settings page is open. */
export function captureInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    emitInstall();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    appInstalled = true;
    emitInstall();
  });
}

/** Test helper: forget captured state. */
export function resetPwaStateForTests(): void {
  deferredPrompt = null;
  appInstalled = false;
  waitingWorker = null;
  installListeners.clear();
  updateListeners.clear();
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const nav = navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true || nav.standalone === true
  );
}

/** iPhone, iPad and iPadOS (which reports itself as a Mac with a touch screen). */
export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (/iPhone|iPad|iPod/.test(navigator.userAgent)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

export type InstallState = 'installed' | 'prompt' | 'ios' | 'generic' | 'unsupported';

function currentInstallState(): InstallState {
  if (appInstalled || isStandalone()) return 'installed';
  if (!('serviceWorker' in navigator)) return 'unsupported';
  if (deferredPrompt) return 'prompt';
  if (isIos()) return 'ios';
  return 'generic';
}

export function useInstallApp(): { state: InstallState; install: () => Promise<void> } {
  const [state, setState] = useState<InstallState>(currentInstallState);
  useEffect(() => {
    const update = () => setState(currentInstallState());
    installListeners.add(update);
    update();
    return () => {
      installListeners.delete(update);
    };
  }, []);
  const install = useCallback(async () => {
    const event = deferredPrompt;
    if (!event) return;
    // The browser allows one prompt per event.
    deferredPrompt = null;
    try {
      await event.prompt();
      const choice = await event.userChoice;
      if (choice.outcome === 'accepted') appInstalled = true;
    } catch {
      // The prompt can be refused (already shown, not allowed): nothing to do.
    }
    emitInstall();
  }, []);
  return { state, install };
}

// ---- Service worker and update prompt ---------------------------------------------------

let waitingWorker: ServiceWorker | null = null;
const updateListeners = new Set<() => void>();

/** A new worker is installed and waiting: the page offers a refresh. */
export function notifyUpdateAvailable(worker: ServiceWorker): void {
  waitingWorker = worker;
  for (const listener of updateListeners) listener();
}

export function useUpdateAvailable(): {
  available: boolean;
  apply: () => void;
  dismiss: () => void;
} {
  const [worker, setWorker] = useState<ServiceWorker | null>(waitingWorker);
  useEffect(() => {
    const update = () => setWorker(waitingWorker);
    updateListeners.add(update);
    update();
    return () => {
      updateListeners.delete(update);
    };
  }, []);
  const apply = useCallback(() => {
    if (!worker) return;
    let reloaded = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    });
    worker.postMessage({ type: 'SKIP_WAITING' });
  }, [worker]);
  const dismiss = useCallback(() => setWorker(null), []);
  return { available: worker !== null, apply, dismiss };
}

/** Production only; never throws. */
export async function registerServiceWorker(): Promise<void> {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    // Only an update counts: the very first install has no controller yet.
    if (registration.waiting && navigator.serviceWorker.controller) {
      notifyUpdateAvailable(registration.waiting);
    }
    registration.addEventListener('updatefound', () => {
      const worker = registration.installing;
      worker?.addEventListener('statechange', () => {
        if (worker.state === 'installed' && navigator.serviceWorker.controller) {
          notifyUpdateAvailable(worker);
        }
      });
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') registration.update().catch(() => undefined);
    });
  } catch {
    // Unsupported or blocked: the app works the same without a worker.
  }
}
